import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, TicketPercent, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeliveryFeeDealLabel } from '@/components/discounts/discount-badges';
import { appliedDiscountList, discountEffectiveState, discountsClient, type Discount } from '@/lib/discounts-client';
import type { CartView, CartViewAppliedDiscount } from '@/lib/draft-order-client';

interface CartCouponSectionProps {
  /** The saved cart — a coupon lives on the server order, never in the staged working copy. */
  cart: CartView;
  editable: boolean;
  /**
   * Staged edits are pending. Applying then would race the save the operator still has to do — an
   * ITEM_DISCOUNT coupon can add reward lines the staged snapshot knows nothing about, and the save
   * would silently take them off again.
   */
  locked: boolean;
  busy: boolean;
  /** Resolves true when the coupon went on — the input clears only then. */
  onApply: (code: string) => Promise<boolean>;
  /** The code to take off — omitted only when the cart holds at most one typed coupon (§C5). */
  onRemove: (code?: string) => void;
}

/**
 * Apply or remove a coupon on the cart — the operator-side counterpart of the customer typing a
 * code at the agent, going through the same backend use-case and the same refusals.
 *
 * The free-text input is the primary path (a code from a campaign the operator may not have open);
 * the dropdown lists the brand's currently-active coded coupons so the common case is one click.
 */
export function CartCouponSection({ cart, editable, locked, busy, onApply, onRemove }: CartCouponSectionProps) {
  const { t } = useTranslation('conversations');
  const deliveryFeeDealLabel = useDeliveryFeeDealLabel();
  const [code, setCode] = useState('');

  // Every discount on the cart, normalised over the deploy window.
  const applied = appliedDiscountList(cart);

  // Only fetched while the control is actually usable — and never for the read-only panel, where
  // the totals rows already tell the whole discount story. Applying is additive now, so it stays
  // fetched while discounts are on the cart.
  const { data: available = [] } = useQuery({
    queryKey: ['cart-coupons', cart.brandId],
    queryFn: async () => (await discountsClient.list()).data,
    enabled: editable,
    staleTime: 60_000,
    select: (coupons) => coupons.filter((coupon) => isApplicableTo(coupon, cart)),
  });

  if (!editable) return null;

  const disabled = locked || busy;

  const submit = async () => {
    const trimmed = code.trim();
    if (!trimmed || disabled) return;
    if (await onApply(trimmed)) setCode('');
  };

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('cart.coupon.title')}</h3>

      {applied.length > 0 && (
        <div className="space-y-1.5">
          {applied.map((discount, index) => (
            <div
              key={discount.discountId || `${discount.code ?? discount.name ?? ''}-${index}`}
              className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <TicketPercent className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <p className="truncate font-medium text-foreground">{discount.code ?? discount.name}</p>
                  {/* Limited to a phone list this customer is on (C5). The only exclusive-promotion
                      chrome the cart carries (D9). */}
                  {discount.exclusive && (
                    <span className="shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                      {t('cart.coupon.exclusive')}
                    </span>
                  )}
                </div>
                {discount.minimumOrderAmount != null && (
                  <p className="text-xs text-muted-foreground">
                    {t('cart.coupon.minimum', {
                      amount: `${cart.currencySymbol}${discount.minimumOrderAmount.toFixed(2)}`,
                    })}
                  </p>
                )}
                <DiscountFeeNotes discount={discount} cart={cart} />
              </div>
              {/* An automatic promotion or the POS's own discount — nobody typed it, nobody removes it. */}
              {!discount.auto && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  disabled={disabled}
                  onClick={() => onRemove(discount.code ?? undefined)}
                  aria-label={t('cart.coupon.remove')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* A sibling of the chips, not their alternative: applying is additive, so a second code has
          to be reachable while the first one is on the cart. */}
      <div className="space-y-1.5">
        <div className="flex gap-2">
          <Input
            value={code}
            disabled={disabled}
            placeholder={t('cart.coupon.placeholder')}
            className="uppercase placeholder:normal-case"
            onChange={(event) => setCode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submit();
            }}
          />
          <Button
            variant="outline"
            loading={busy}
            disabled={disabled || code.trim().length === 0}
            onClick={() => void submit()}
          >
            {t('cart.actions.apply')}
          </Button>
        </div>

        {available.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={disabled}>
                <TicketPercent className="h-3.5 w-3.5" />
                {t('cart.coupon.available', { count: available.length })}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
              {available.map((coupon) => (
                <DropdownMenuItem
                  key={coupon.id}
                  onSelect={() => void onApply(coupon.code as string)}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="font-medium">{coupon.code}</span>
                  <span className="text-xs text-muted-foreground">
                    {coupon.type === 'DELIVERY_FEE' ? deliveryFeeDealLabel(coupon) : couponValueLabel(coupon, cart, t)}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {locked && <p className="text-xs text-muted-foreground">{t('cart.coupon.saveFirst')}</p>}
      </div>
    </section>
  );
}

/**
 * Whether the coupon is worth offering for THIS cart: coded (an automatic promotion applies
 * itself), currently active, and running at the cart's branch. A cart not yet pinned to a branch
 * sees everything — there is no scope to check against, and the confirmation gate re-judges anyway.
 */
function isApplicableTo(coupon: Discount, cart: CartView): boolean {
  if (coupon.code == null) return false;
  if (discountEffectiveState(coupon) !== 'active') return false;
  if (coupon.locationIds?.length && cart.locationId && !coupon.locationIds.includes(cart.locationId)) return false;
  return true;
}

/**
 * The fee side of one applied discount: what it took off the delivery fee ("Envío gratis" when it
 * cleared the whole fee, "Envío −$15.00" otherwise), and the server's `DELIVERY_TYPE` verdict (C5)
 * when the cart's fulfilment keeps it from earning — never inferred here from the type.
 */
function DiscountFeeNotes({ discount, cart }: { discount: CartViewAppliedDiscount; cart: CartView }) {
  const { t } = useTranslation('conversations');
  const share = discount.deliveryFeeDiscountAmount ?? 0;
  const unmetDeliveryType = discount.unmetConditions?.includes('DELIVERY_TYPE') ?? false;
  return (
    <>
      {share > 0 && (
        <p className="text-xs text-muted-foreground">
          {share >= (cart.deliveryFee ?? 0)
            ? t('cart.coupon.freeDelivery')
            : t('cart.coupon.deliveryFeeShare', { amount: `${cart.currencySymbol}${share.toFixed(2)}` })}
        </p>
      )}
      {/* A delivery cart failing it holds a pickup-only promotion; anything else is waiting on delivery. */}
      {unmetDeliveryType && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {cart.deliveryType === 'delivery' ? t('cart.coupon.unmet.pickupOnly') : t('cart.coupon.unmet.deliveryOnly')}
        </p>
      )}
    </>
  );
}

/** What the code is worth, at a glance — the dropdown's second column. */
function couponValueLabel(
  coupon: Discount,
  cart: CartView,
  t: ReturnType<typeof useTranslation<'conversations'>>['t'],
): string {
  if (coupon.type === 'CART_PERCENT') return `-${coupon.value}%`;
  if (coupon.type === 'CART_FIXED') return `-${cart.currencySymbol}${coupon.value.toFixed(2)}`;
  return t('cart.coupon.itemReward');
}
